import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  type Layer,
  type LayerBase,
  type LayerType,
} from './types/layer';

/**
 * The layer a host gets when they press "Add". Every field is filled in: the
 * renderer never has to guess, and the properties panel never binds to
 * `undefined`.
 */

function base(id: string, name: string, zIndex: number): LayerBase {
  // A new layer lands as a centred quarter-size box rather than filling the
  // frame, so it is visible, selectable and obviously not the background.
  const width = CANVAS_WIDTH / 2;
  const height = CANVAS_HEIGHT / 2;
  return {
    id,
    name,
    x: (CANVAS_WIDTH - width) / 2,
    y: (CANVAS_HEIGHT - height) / 2,
    width,
    height,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    zIndex,
  };
}

export function createLayer(type: LayerType, id: string, zIndex: number): Layer {
  switch (type) {
    case 'auto':
      return {
        // Full frame, not the centred quarter every other layer gets: an
        // automatic grid of participants is almost always the background of a
        // scene rather than an element placed on top of one.
        ...base(id, 'Participants', zIndex),
        x: 0,
        y: 0,
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        type: 'auto',
        arrangement: 'grid',
        gap: 16,
        fit: 'cover',
        cornerRadius: 0,
      };
    case 'video':
      return {
        ...base(id, 'Video', zIndex),
        type: 'video',
        trackSid: null,
        participantIdentity: null,
        trackName: null,
        fit: 'cover',
        crop: null,
        mirrored: false,
      };
    case 'screenshare':
      return {
        ...base(id, 'Screen share', zIndex),
        type: 'screenshare',
        crop: null,
        trackSid: null,
        participantIdentity: null,
        fit: 'contain',
      };
    case 'image':
      return { ...base(id, 'Image', zIndex), type: 'image', url: '', fit: 'contain' };
    case 'text':
      return {
        ...base(id, 'Text', zIndex),
        type: 'text',
        text: 'New text',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 72,
        fontWeight: 600,
        color: '#ffffff',
        align: 'left',
        lineHeight: 1.2,
        strokeColor: null,
        strokeWidth: 0,
      };
    case 'shape':
      return {
        ...base(id, 'Shape', zIndex),
        type: 'shape',
        shape: 'rect',
        fill: '#3b82f6',
        strokeColor: null,
        strokeWidth: 0,
        cornerRadius: 0,
      };
    case 'template':
      return { ...base(id, 'Template', zIndex), type: 'template', templateId: null, values: {} };
  }
}
